// Hotfix 5.0 — Anchor & Ripple engine.
// The ring the user engages FIRST is the "anchor"; the remaining rings are constrained
// ("rippled") from it. This module derives the supply/demand bridge so the UI can filter
// options, drive the target dial, and flash a ring red when a constraint is infeasible.
//
// 100% baselines (per the product spec):
//   Location (canvas) : Total Viable Area  (max spatial potential — climate/shadow/typology)
//   System (hardware) : Utilized Area       (consumes Viable Area)
//   Crops (output)    : Yield Target         (kg to feed the user-defined population)
//   Resources (loop)  : Recovery %           (operating demand met on-site)

// Per-capita produce intake — user-confirmed baseline for the Crops "yield target".
export const KG_PER_PERSON_YR = 230;
// Nominal BIA productivity (kg produce / m² / yr) used when no instance yet gives a measured rate.
const NOMINAL_YIELD_DENSITY = 30;

export const SCENARIOS = {
  locations: { key: "A", label: "Supply-driven", hint: "Allocated area pushes a m² wallet forward and caps crop yield." },
  systems: { key: "C", label: "Tech-driven", hint: "Hardware filters viable locations and sets crop yield ceilings." },
  crops: { key: "B", label: "Demand-driven", hint: "Yield target works backward to the m² and hardware you need." },
};

// Footprint (m²) a system needs at minimum — fixed pods enforce it; others are flexible (0).
export function systemMinFootprint(sys) {
  const ff = sys?.fixed_footprint;
  if (ff && ff.length && ff.width) return +(ff.length * ff.width).toFixed(1);
  const fp = sys?.specs?.footprint_m2;
  return fp ? Number(fp) : 0;
}

export function computeRipple({ anchorRing, opportunity, occupancy, systemBlocks = [], pairDraft, kpis, grossAreas, selections }) {
  const fullViableArea = Math.max(0, opportunity?.opportunity_area || 0);

  // Gross area of each candidate surface (roof / ground / façade-by-orientation).
  const locArea = (id) => {
    if (!grossAreas || !id) return 0;
    if (id === "rooftop") return grossAreas.roof || 0;
    if (id === "ground") return grossAreas.base || 0;
    if (id.startsWith("facade_")) return grossAreas.facade?.[id.split("_")[1]?.toUpperCase()] || 0;
    return 0;
  };
  // Which surface(s) is the user building on? (committed blocks, the in-progress draft, else the
  // Zone-ring selection.)
  const pickedIds = new Set();
  systemBlocks.forEach((b) => b.locationId && pickedIds.add(b.locationId));
  if (pairDraft?.locationId) pickedIds.add(pairDraft.locationId);
  if (pickedIds.size === 0) (selections?.locations || []).forEach((id) => pickedIds.add(id));
  let pickedLocationArea = 0;
  pickedIds.forEach((id) => { pickedLocationArea += locArea(id); });

  // #1 — Viable area scopes to the SELECTED sub-zone. When the user has picked specific surfaces,
  // "viable" reflects only those surfaces. We use the backend's per-surface viable_breakdown (which
  // applies the correct per-surface reserves — roof MEP/reserve, façade orientation, etc.) so the
  // scoped figure and the "Viable area by surface" breakdown match Building Configuration exactly.
  // Falls back to a whole-site opportunity ratio only for surfaces without a per-surface figure.
  const totalGross = grossAreas ? ((grossAreas.base || 0) + (grossAreas.roof || 0) + (grossAreas.facade?.total || 0)) : 0;
  const oppRatio = totalGross > 0 ? fullViableArea / totalGross : 1;
  const vb = opportunity?.viable_breakdown || null;
  const viableOf = (id) => {
    if (!vb || !id) return null;
    if (id === "rooftop") return vb.roof ?? null;
    if (id === "ground") return vb.base ?? null;
    if (id === "indoor") return vb.interior ?? null;
    if (id.startsWith("facade_")) return vb.facade?.[id.split("_")[1]?.toUpperCase()] ?? null;
    return null;
  };
  let pickedViable = 0, haveAllViable = vb != null && pickedIds.size > 0;
  pickedIds.forEach((id) => { const v = viableOf(id); if (v == null) haveAllViable = false; else pickedViable += v; });
  const surfacesPicked = pickedLocationArea > 0 && pickedLocationArea < totalGross - 0.5;
  const viableArea = surfacesPicked
    ? (haveAllViable ? +pickedViable.toFixed(1) : +(pickedLocationArea * oppRatio).toFixed(1))
    : fullViableArea;

  const committed = systemBlocks.reduce((s, b) => s + (Number(b.area) || 0), 0);
  const draftArea = pairDraft ? Number(pairDraft.area) || 0 : 0;
  const utilizedArea = +(committed + draftArea).toFixed(1);
  const walletArea = +Math.max(0, viableArea - utilizedArea).toFixed(1);

  // ── 3-Tier Allocation Funnel: Viable Area ⊇ Allocated Area ⊇ System Footprint ──
  // System Footprint = the actual physical ground space the hardware occupies (utilizedArea).
  // Allocated Area   = the sub-section of Viable Area the user explicitly zones/dedicates.
  //                    Backward-compat: an instance with no explicit allocation defaults to its
  //                    own footprint, so footprint == allocated and no false warning fires.
  const systemFootprint = utilizedArea;
  const allocOf = (b) => (Number(b.allocatedArea) > 0 ? Number(b.allocatedArea) : (Number(b.area) || 0));
  // P3: many systems can share ONE location Allocated Area. Count each location's allocation once
  // (nested hosting), so the funnel's Allocated tier isn't inflated by stacking systems.
  const seenLoc = new Set();
  let allocatedFromBlocks = 0;
  systemBlocks.forEach((b) => {
    if (b.locationId) {
      if (seenLoc.has(b.locationId)) return;
      seenLoc.add(b.locationId);
    }
    allocatedFromBlocks += allocOf(b);
  });
  const draftLocCounted = pairDraft?.locationId && seenLoc.has(pairDraft.locationId);
  const draftAllocated = (pairDraft && !draftLocCounted)
    ? (Number(pairDraft.allocatedArea) > 0 ? Number(pairDraft.allocatedArea) : draftArea)
    : 0;
  const allocatedArea = +(allocatedFromBlocks + draftAllocated).toFixed(1);
  // Warn (never block) when hardware overflows the zoned area.
  const footprintExceedsAllocated = allocatedArea > 0 && systemFootprint > allocatedArea + 0.5;
  // Raw (UNCLAMPED) ring ratios — preserved even beyond 100% so tooltips can surface the true math.
  const locFillRaw = viableArea > 0 ? allocatedArea / viableArea : 0;
  const sysFillRaw = allocatedArea > 0 ? systemFootprint / allocatedArea : 0;

  // Picked-location gross area — the System ring fills toward 100% = the full surface area of the
  // location(s) the user is building on (e.g. a 144 m² roof), not the whole-site viable area.
  if (pickedLocationArea <= 0) pickedLocationArea = viableArea; // fallback for surfaces w/o a gross area

  const yieldTarget = Math.max(0, Math.round((Number(occupancy) || 0) * KG_PER_PERSON_YR));
  const yieldRealized = Math.max(0, Math.round(kpis?.total_yield || 0));
  const yieldGap = Math.max(0, yieldTarget - yieldRealized);

  const density = committed > 0 && yieldRealized > 0 ? yieldRealized / committed : NOMINAL_YIELD_DENSITY;
  const requiredAreaForTarget = yieldTarget > 0 ? Math.round(yieldTarget / density) : 0;

  // Constraint failure detection (hard, site-level) + funnel warning (soft, zone-level).
  let constraint = { failed: false, ring: null, message: "" };
  if (viableArea > 0 && utilizedArea > viableArea + 0.5) {
    constraint = {
      failed: true, ring: "systems",
      message: `Systems occupy ${Math.round(utilizedArea).toLocaleString()} m² but only ${Math.round(viableArea).toLocaleString()} m² is viable — reduce allocated area.`,
    };
  } else if (yieldTarget > 0 && viableArea > 0 && requiredAreaForTarget > viableArea) {
    constraint = {
      failed: true, ring: "crops",
      message: `Target needs ≈${requiredAreaForTarget.toLocaleString()} m² of growing area, but only ${Math.round(viableArea).toLocaleString()} m² is viable — expand the site or lower the target.`,
    };
  }
  // Soft funnel warning: hardware footprint spilling past the zoned allocation (warn, not block).
  const warn = footprintExceedsAllocated
    ? { ring: "systems", message: `System footprint ${Math.round(systemFootprint).toLocaleString()} m² exceeds the ${Math.round(allocatedArea).toLocaleString()} m² you allocated — zone more area or shrink the hardware.` }
    : null;

  const scenario = anchorRing ? SCENARIOS[anchorRing] : null;
  return {
    anchorRing, scenario,
    viableArea, fullViableArea, utilizedArea, walletArea, pickedLocationArea: +pickedLocationArea.toFixed(1),
    allocatedArea, systemFootprint, footprintExceedsAllocated, locFillRaw, sysFillRaw, warn,
    yieldTarget, yieldRealized, yieldGap,
    density: +density.toFixed(1), requiredAreaForTarget,
    constraint,
  };
}
